import type { EntityId } from "@note-app/contracts";
import type { Clock } from "@note-app/domain";
import {
  validateTrashRetentionPolicy,
  type PurgeResult,
  type TrashRetentionPolicy,
  type UnitOfWork,
} from "@note-app/storage";

export interface TrashOperationResult {
  readonly purged: readonly PurgeResult[];
  readonly detachedAttachmentIds: readonly EntityId[];
}

export class TrashApplicationService {
  public constructor(
    private readonly unitOfWork: UnitOfWork,
    private readonly clock: Clock,
  ) {}

  public loadPolicy(): Promise<TrashRetentionPolicy> {
    return this.unitOfWork.repositories.trash.getRetentionPolicy();
  }

  public updatePolicy(
    policy: TrashRetentionPolicy,
  ): Promise<TrashRetentionPolicy> {
    validateTrashRetentionPolicy(policy);
    return this.unitOfWork.repositories.trash.setRetentionPolicy(
      policy,
      this.clock.now(),
    );
  }

  public async purgeItem(input: {
    itemId: EntityId;
    expectedRevision: number;
  }): Promise<TrashOperationResult> {
    const purged = [
      await this.unitOfWork.repositories.trash.purgeItem(
        input.itemId,
        input.expectedRevision,
      ),
    ];
    return summarize(purged);
  }

  public async purgeItems(
    targets: readonly {
      readonly itemId: EntityId;
      readonly expectedRevision: number;
    }[],
  ): Promise<TrashOperationResult> {
    const ids = new Set<EntityId>();
    for (const target of targets) {
      if (ids.has(target.itemId)) {
        throw new RangeError("Purge targets must be unique.");
      }
      ids.add(target.itemId);
    }
    const purged = await this.unitOfWork.transaction(async (repositories) => {
      const results: PurgeResult[] = [];
      for (const target of targets) {
        results.push(
          await repositories.trash.purgeItem(
            target.itemId,
            target.expectedRevision,
          ),
        );
      }
      return results;
    });
    return summarize(purged);
  }

  public async empty(): Promise<TrashOperationResult> {
    return summarize(await this.unitOfWork.repositories.trash.empty());
  }

  public async runRetention(): Promise<TrashOperationResult> {
    const policy =
      await this.unitOfWork.repositories.trash.getRetentionPolicy();
    if (!policy.automaticCleanup) {
      return { purged: [], detachedAttachmentIds: [] };
    }
    const cutoff =
      this.clock.now() - policy.retentionDays * 24 * 60 * 60 * 1_000;
    return summarize(
      await this.unitOfWork.repositories.trash.purgeDeletedBefore(cutoff),
    );
  }
}

function summarize(purged: readonly PurgeResult[]): TrashOperationResult {
  return {
    purged,
    detachedAttachmentIds: [
      ...new Set(purged.flatMap((result) => result.detachedAttachmentIds)),
    ],
  };
}
